import { useMutation, useQueryClient } from "react-query";

import { deleteGoal, type TargetDisposition } from "api/goals";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_LIST_OF_GOALS } from "@constants/reactQueryKeys";

interface DeleteGoalPayload {
  goalId: number;
  clientId: string;
  targetDispositions?: TargetDisposition[];
  rationale: string;
}

const useDeleteGoal = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: deleteGoalAction,
    isLoading: isDeletingGoal,
    ...rest
  } = useMutation(
    ({ goalId, targetDispositions, rationale }: DeleteGoalPayload) =>
      deleteGoal(goalId, rationale, targetDispositions),
    {
      onSuccess: (response, variables) => {
        showToast({ message: "Goal deleted" });

        queryClient.invalidateQueries([GET_LIST_OF_GOALS, variables.clientId]);

        onSuccess(response, variables);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || "Failed to delete goal";
        showToast({ message: errorMessage, errorState: true });

        onError(error);
      },
      ...otherOptions,
    }
  );

  return {
    deleteGoalAction,
    isDeletingGoal,
    ...rest,
  };
};

export default useDeleteGoal;
